import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AreaChart, Area, XAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { useTenant } from '../../context/TenantContext'; 
import { getTenantContractor, updateTenantContractor } from '../../services/crmService'; 
import { db } from '../../services/firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';

// --- SLIDE-OVER COMPONENT ---
function EditContractorSlideOver({ contractor, locations, loadingLocations, onClose, onSave }) {
  const [formData, setFormData] = useState({
    name: contractor.name,
    email: contractor.email,
    phone: contractor.phone,
    assignedTenant: contractor.assignedTenant,
    operatingCity: contractor.operatingCity,
    status: contractor.status,
    tier: contractor.tier,
    currentMrr: String(contractor.metrics.currentMrr).replace(/[^0-9.]/g, ''), 
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const updatedContractor = {
      ...contractor,
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      assignedTenant: formData.assignedTenant,
      operatingCity: formData.operatingCity,
      status: formData.status,
      tier: formData.tier,
      metrics: {
        ...contractor.metrics,
        currentMrr: parseFloat(formData.currentMrr) || 0,
      }
    };
    onSave(updatedContractor);
  };

  return (
    <>
      <div 
        className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-40 animate-in fade-in duration-300"
        onClick={onClose}
      ></div>

      <div className="fixed inset-y-0 right-0 w-full max-w-md bg-slate-50 shadow-2xl z-50 flex flex-col animate-in slide-in-from-right duration-300 border-l border-slate-300">
        <div className="bg-slate-900 p-6 flex justify-between items-center border-b border-slate-800 shrink-0">
          <div>
            <h2 className="text-lg font-black text-white uppercase tracking-widest">Edit Fleet Operator</h2>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">ID: {contractor.id}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors">
            <i className="fa-solid fa-xmark text-xl"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
          <form id="edit-contractor-form" onSubmit={handleSubmit} className="space-y-6">
            
            <div className="space-y-4">
              <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest border-b border-slate-200 pb-2">Identity & Status</h3>
              <div>
                <label className="block text-[10px] font-bold text-slate-600 uppercase tracking-widest mb-1">Full Name</label>
                <input 
                  type="text" name="name" value={formData.name} onChange={handleChange} required
                  className="w-full bg-white border border-slate-300 text-slate-900 text-sm font-bold px-3 py-2 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-bold text-slate-600 uppercase tracking-widest mb-1">Status</label>
                  <select 
                    name="status" value={formData.status} onChange={handleChange}
                    className="w-full bg-white border border-slate-300 text-slate-900 text-sm font-bold px-3 py-2 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 cursor-pointer"
                  >
                    <option value="Active">Active</option>
                    <option value="Onboarding">Onboarding</option>
                    <option value="Suspended">Suspended</option>
                    <option value="Terminated">Terminated</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-600 uppercase tracking-widest mb-1">Fleet Tier</label>
                  <select 
                    name="tier" value={formData.tier} onChange={handleChange}
                    className="w-full bg-white border border-slate-300 text-slate-900 text-sm font-bold px-3 py-2 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 cursor-pointer"
                  >
                    <option value="Standard 1099">Standard 1099</option>
                    <option value="Gold 1099">Gold 1099</option>
                    <option value="Platinum 1099">Platinum 1099</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="space-y-4 pt-2">
              <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest border-b border-slate-200 pb-2">Contact & Routing</h3>
              <div>
                <label className="block text-[10px] font-bold text-slate-600 uppercase tracking-widest mb-1">Email Address</label>
                <input 
                  type="email" name="email" value={formData.email} onChange={handleChange} required
                  className="w-full bg-white border border-slate-300 text-slate-900 text-sm font-bold px-3 py-2 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-[10px] font-bold text-slate-600 uppercase tracking-widest mb-1">Operating Assignment</label>
                  <select 
                    name="assignedTenant" 
                    value={formData.assignedTenant} 
                    onChange={handleChange} 
                    disabled={loadingLocations}
                    className="w-full bg-white border border-slate-300 text-slate-900 text-sm font-bold px-3 py-2 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 cursor-pointer disabled:opacity-50"
                  >
                    <option value="">{loadingLocations ? 'Fetching...' : 'Select Assignment...'}</option>
                    {locations.map(loc => (
                      <option key={loc.id} value={loc.storeName || loc.name}>
                        {loc.storeName || loc.name}
                      </option>
                    ))}
                    <option value="Remote / Field Sales">Remote / Field Sales</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-600 uppercase tracking-widest mb-1">Direct Phone</label>
                  <input 
                    type="text" name="phone" value={formData.phone} onChange={handleChange} required
                    className="w-full bg-white border border-slate-300 text-slate-900 text-sm font-bold px-3 py-2 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-600 uppercase tracking-widest mb-1">Base City</label>
                  <input 
                    type="text" name="operatingCity" value={formData.operatingCity} onChange={handleChange} required
                    placeholder="e.g. Seattle"
                    className="w-full bg-white border border-slate-300 text-slate-900 text-sm font-bold px-3 py-2 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-4 pt-2">
              <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest border-b border-slate-200 pb-2">Manual Adjustments</h3>
              <div>
                <label className="block text-[10px] font-bold text-slate-600 uppercase tracking-widest mb-1">Current MRR Override ($)</label>
                <input 
                  type="number" name="currentMrr" value={formData.currentMrr} onChange={handleChange}
                  className="w-full bg-white border border-slate-300 text-slate-900 text-sm font-bold px-3 py-2 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                />
              </div>
            </div>

          </form>
        </div>

        <div className="p-6 bg-white border-t border-slate-300 flex gap-4 shrink-0">
          <button 
            type="button" 
            onClick={onClose}
            className="flex-1 bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 text-[10px] font-bold py-3 uppercase tracking-widest transition-colors"
          >
            Cancel
          </button>
          <button 
            type="submit" 
            form="edit-contractor-form"
            className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white border border-emerald-700 shadow-sm text-[10px] font-bold py-3 uppercase tracking-widest transition-colors"
          >
            Save Profile
          </button>
        </div>
      </div>
    </>
  );
}

// --- MAIN PROFILE COMPONENT ---
export default function ContractorProfile() {
  const { id } = useParams();
  const { tenantId, loading: tenantLoading } = useTenant();

  const [showEditForm, setShowEditForm] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [contractor, setContractor] = useState(null);

  // Dynamic Locations State
  const [locations, setLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);

  // --- FETCH ACTIVE SUB-LOCATIONS/DIVISIONS ---
  useEffect(() => {
    const fetchLocations = async () => {
      if (tenantLoading || !tenantId) return;
      
      setLoadingLocations(true);
      try {
        const storesRef = collection(db, 'clients', tenantId, 'stores');
        const q = query(storesRef, where('isActive', '==', true));
        const snapshot = await getDocs(q);
        
        const fetchedLocations = [];
        snapshot.forEach(doc => {
          fetchedLocations.push({ id: doc.id, ...doc.data() });
        });
        
        setLocations(fetchedLocations);
      } catch (error) {
        console.error("Failed to fetch locations:", error);
      } finally {
        setLoadingLocations(false);
      }
    };

    fetchLocations();
  }, [tenantId, tenantLoading]);

  // --- ⚡️ LIVE FIREBASE FETCH ⚡️ ---
  useEffect(() => {
    const fetchContractorProfile = async () => {
      if (tenantLoading || !tenantId || !id) return;
      
      setIsLoading(true);
      try {
        const rawData = await getTenantContractor(tenantId, id);
        
        if (rawData) {
          setContractor({
            id: rawData.id.substring(0, 8),
            rawId: rawData.id, 
            name: `${rawData.firstName || rawData.first_name || ''} ${rawData.lastName || rawData.last_name || ''}`.trim() || 'Unknown Rep',
            email: rawData.email || 'No email provided',
            phone: rawData.phone || 'No phone provided',
            assignedTenant: rawData.assignedTenant || 'Remote / Field Sales',
            operatingCity: rawData.operatingCity || '',
            location: rawData.operatingCity ? `${rawData.operatingCity}, ${rawData.operatingState || ''}`.trim() : (rawData.location || 'Unknown Region'),
            status: rawData.status || 'Active',
            tier: rawData.commissionTier || 'Standard 1099',
            compliance: rawData.compliance || { w9: false, nda: false, ica: false },
            checkHqVaulted: !!rawData.checkhq_id, // Integrates CheckHQ sync status
            metrics: {
              ltv: rawData.metrics?.ltv || 0,
              currentMrr: rawData.metrics?.current_mrr || 0,
              retention: rawData.metrics?.retention_rate || 100,
              ytdPayout: rawData.metrics?.ytd_payout || 0
            }
          });
        }
      } catch (error) {
        console.error("Failed to fetch contractor profile:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchContractorProfile();
  }, [tenantId, tenantLoading, id]);

  const performanceData = [
    { month: 'Nov', revenue: 8500 },
    { month: 'Dec', revenue: 9200 },
    { month: 'Jan', revenue: 10100 },
    { month: 'Feb', revenue: 11400 },
    { month: 'Mar', revenue: 12000 },
    { month: 'Apr', revenue: 12500 },
  ];

  const toggleCompliance = async (docType) => {
    if (!contractor) return;
    
    const newCompliance = {
      ...contractor.compliance,
      [docType]: !contractor.compliance[docType]
    };
    
    setContractor(prev => ({
      ...prev,
      compliance: newCompliance
    }));

    try {
      await updateTenantContractor(tenantId, contractor.rawId, {
        compliance: newCompliance
      });
    } catch (error) {
      console.error("Failed to sync compliance update:", error);
    }
  };

  const handleSaveProfile = async (updatedData) => {
    if (!tenantId || !contractor) return;
    
    setContractor(updatedData);
    setShowEditForm(false);

    try {
      // Safely parse name back to schema standards
      const nameParts = updatedData.name.split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      await updateTenantContractor(tenantId, contractor.rawId, {
        first_name: firstName,
        last_name: lastName,
        firstName: firstName, // Backup mapping
        lastName: lastName,   // Backup mapping
        status: updatedData.status,
        commissionTier: updatedData.tier,
        email: updatedData.email,
        phone: updatedData.phone,
        operatingCity: updatedData.operatingCity,
        assignedTenant: updatedData.assignedTenant, // Dynamic mapping saved back to DB
        metrics: {
          current_mrr: updatedData.metrics.currentMrr
        }
      });
    } catch (error) {
      console.error("Failed to sync profile update:", error);
    }
  };

  if (isLoading || tenantLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin mb-4"></div>
        <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Loading Profile...</p>
      </div>
    );
  }

  if (!contractor) {
    return (
      <div className="p-12 text-center text-slate-500 text-sm font-bold uppercase tracking-widest">
        Contractor record not found.
      </div>
    );
  }

  // W-9 is automatically cleared if the CheckHQ API confirms they are vaulted securely
  const isW9Cleared = contractor.checkHqVaulted || contractor.compliance.w9;
  const isAllCleared = isW9Cleared && contractor.compliance.nda && contractor.compliance.ica;
  
  // Safe monogram generator
  const getMonogram = (name) => {
    const parts = name.split(' ');
    if (parts.length > 1) return (parts[0][0] + parts[1][0]).toUpperCase();
    if (parts[0].length > 0) return parts[0].substring(0, 2).toUpperCase();
    return '??';
  };

  return (
    <div className="animate-in fade-in duration-500 pb-12 relative">
      
      <div className="mb-6">
        <Link to="/contractors" className="text-[10px] font-bold text-slate-400 hover:text-emerald-600 uppercase tracking-widest transition-colors flex items-center gap-2 w-fit">
          <i className="fa-solid fa-arrow-left"></i> Back to Fleet Ledger
        </Link>
      </div>

      <div className="bg-white border border-slate-300 shadow-sm p-6 mb-6 flex justify-between items-start">
        <div className="flex items-center gap-5">
          <div className="w-16 h-16 bg-slate-900 text-white flex items-center justify-center font-black text-2xl border border-slate-700 shadow-inner">
            {getMonogram(contractor.name)}
          </div>
          <div>
            <div className="flex items-center gap-3 mb-1">
              <h1 className="text-2xl font-black text-slate-900 tracking-tight uppercase">{contractor.name}</h1>
              <span className={`border text-[9px] font-bold px-2 py-0.5 uppercase tracking-widest ${
                contractor.status === 'Active' ? 'border-emerald-500 text-emerald-600 bg-emerald-50' : 
                contractor.status === 'Terminated' ? 'border-red-500 text-red-600 bg-red-50' :
                'border-amber-500 text-amber-600 bg-amber-50'
              }`}>
                {contractor.status}
              </span>
              {/* NEW: Live CheckHQ Profile Indicator */}
              {contractor.checkHqVaulted ? (
                <span className="border border-emerald-500 text-emerald-600 bg-emerald-50 text-[9px] font-bold px-2 py-0.5 uppercase tracking-widest flex items-center gap-1">
                  <i className="fa-solid fa-lock"></i> CheckHQ Vaulted
                </span>
              ) : (
                <span className="border border-amber-500 text-amber-600 bg-amber-50 text-[9px] font-bold px-2 py-0.5 uppercase tracking-widest flex items-center gap-1 animate-pulse">
                  <i className="fa-solid fa-lock-open"></i> Awaiting CheckHQ
                </span>
              )}
            </div>
            <p className="text-slate-500 text-sm font-medium">Operator ID: <span className="font-bold text-slate-700">{contractor.id}</span> • {contractor.tier}</p>
          </div>
        </div>
        <div className="flex gap-3">
          <button 
            onClick={() => setShowEditForm(true)}
            className="bg-white hover:bg-slate-50 text-slate-700 border border-slate-300 text-[10px] font-bold py-2 px-4 uppercase tracking-widest transition-colors"
          >
            Edit Profile
          </button>
          <button className="bg-slate-900 hover:bg-slate-800 text-white border border-slate-900 text-[10px] font-bold py-2 px-4 uppercase tracking-widest shadow-sm transition-colors flex items-center gap-2">
            <i className="fa-solid fa-paper-plane"></i> Message
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        <div className="space-y-6">
          <div className="bg-white border border-slate-300 shadow-sm">
            <div className="bg-slate-100 border-b border-slate-300 px-4 py-3">
              <h3 className="text-[10px] font-bold text-slate-600 uppercase tracking-widest">Contact & Routing Identity</h3>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1">Primary Email</p>
                <a href={`mailto:${contractor.email}`} className="text-sm font-bold text-emerald-600 hover:text-emerald-800 transition-colors flex items-center gap-2">
                  {contractor.email} <i className="fa-solid fa-arrow-up-right-from-square text-[10px]"></i>
                </a>
              </div>
              <div>
                <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1">Direct Phone</p>
                <a href={`tel:${contractor.phone.replace(/[^0-9]/g, '')}`} className="text-sm font-bold text-emerald-600 hover:text-emerald-800 transition-colors flex items-center gap-2">
                  {contractor.phone} <i className="fa-solid fa-phone text-[10px]"></i>
                </a>
              </div>
              <div>
                <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1">Operating Region</p>
                <p className="text-sm font-bold text-slate-800">{contractor.location}</p>
              </div>
              <div className="border-t border-slate-100 pt-3">
                <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1">Assignment Division</p>
                <p className="text-sm font-black text-indigo-600"><i className="fa-solid fa-network-wired mr-2"></i>{contractor.assignedTenant || 'Remote / Field Sales'}</p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-slate-300 shadow-sm">
            <div className="bg-slate-100 border-b border-slate-300 px-4 py-3 flex justify-between items-center">
              <h3 className="text-[10px] font-bold text-slate-600 uppercase tracking-widest">Compliance Vault</h3>
              <span className={`text-[8px] font-black uppercase tracking-widest px-2 py-0.5 ${isAllCleared ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                {isAllCleared ? 'Cleared' : 'Hold Imposed'}
              </span>
            </div>
            <div className="p-4 space-y-3">
              <p className="text-[9px] font-bold text-slate-500 uppercase tracking-widest mb-3">Click status icon to toggle verification.</p>
              
              <div 
                onClick={() => { if (!contractor.checkHqVaulted) toggleCompliance('w9'); }} 
                className="flex justify-between items-center cursor-pointer group p-2 hover:bg-slate-50 border border-transparent hover:border-slate-100 transition-all rounded-sm"
              >
                <span className="text-xs font-bold text-slate-700 group-hover:text-emerald-600 transition-colors">W-9 Form (CheckHQ Sync)</span>
                <i className={`fa-solid fa-circle-check transition-colors ${isW9Cleared ? 'text-emerald-500' : 'text-slate-300 group-hover:text-amber-400'}`}></i>
              </div>
              
              <div 
                onClick={() => toggleCompliance('nda')} 
                className="flex justify-between items-center cursor-pointer group p-2 hover:bg-slate-50 border border-transparent hover:border-slate-100 transition-all rounded-sm"
              >
                <span className="text-xs font-bold text-slate-700 group-hover:text-emerald-600 transition-colors">Non-Disclosure (NDA)</span>
                <i className={`fa-solid fa-circle-check transition-colors ${contractor.compliance.nda ? 'text-emerald-500' : 'text-slate-300 group-hover:text-amber-400'}`}></i>
              </div>
              
              <div 
                onClick={() => toggleCompliance('ica')} 
                className="flex justify-between items-center cursor-pointer group p-2 hover:bg-slate-50 border border-transparent hover:border-slate-100 transition-all rounded-sm"
              >
                <span className="text-xs font-bold text-slate-700 group-hover:text-emerald-600 transition-colors">Independent Auth (ICA)</span>
                <i className={`fa-solid fa-circle-check transition-colors ${contractor.compliance.ica ? 'text-emerald-500' : 'text-slate-300 group-hover:text-amber-400'}`}></i>
              </div>

              {!isAllCleared && (
                <button className="w-full mt-4 bg-slate-50 hover:bg-slate-100 text-slate-600 border border-slate-200 text-[9px] font-bold py-2 uppercase tracking-widest transition-colors">
                  {!contractor.checkHqVaulted ? 'Resend CheckHQ Invite' : 'Request Document Update'}
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="lg:col-span-2 space-y-6">
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-white border border-slate-300 shadow-sm p-4 border-t-4 border-t-emerald-500">
              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1">Current MRR</p>
              <p className="text-xl font-black text-slate-800">${Number(contractor.metrics.currentMrr).toLocaleString()}</p>
            </div>
            <div className="bg-white border border-slate-300 shadow-sm p-4 border-t-4 border-t-blue-500">
              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1">Retention Rate</p>
              <p className="text-xl font-black text-slate-800">{contractor.metrics.retention}%</p>
            </div>
            <div className="bg-white border border-slate-300 shadow-sm p-4 border-t-4 border-t-amber-500">
              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1">YTD Payouts</p>
              <p className="text-xl font-black text-slate-800">${Number(contractor.metrics.ytdPayout).toLocaleString()}</p>
            </div>
          </div>

          <div className="bg-slate-900 border border-slate-800 shadow-xl flex flex-col h-[280px]">
            <div className="p-4 border-b border-slate-800 flex justify-between items-center">
              <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2">
                <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span> Personal MRR Trajectory (6 Mo)
              </h3>
            </div>
            <div className="p-4 flex-1">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={performanceData} margin={{ top: 5, right: 0, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorContractorRev" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="month" stroke="#64748B" fontSize={10} tickLine={false} axisLine={false} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#0F172A', border: '1px solid #1E293B', borderRadius: '0px' }}
                    itemStyle={{ color: '#10B981', fontSize: '12px', fontWeight: 'bold' }}
                    labelStyle={{ color: '#64748B', fontSize: '10px', textTransform: 'uppercase', marginBottom: '4px' }}
                    formatter={(value) => [`$${value.toLocaleString()}`, 'MRR']}
                  />
                  <Area type="monotone" dataKey="revenue" stroke="#10B981" strokeWidth={2} fillOpacity={1} fill="url(#colorContractorRev)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white border border-slate-300 shadow-sm">
            <div className="bg-slate-100 border-b border-slate-300 px-4 py-3 flex justify-between items-center">
              <h3 className="text-[10px] font-bold text-slate-600 uppercase tracking-widest">Recent Activity Ledger</h3>
            </div>
            <div className="divide-y divide-slate-100">
              <div className="px-4 py-3 flex justify-between items-center hover:bg-slate-50 transition-colors">
                <div>
                  <p className="text-xs font-bold text-slate-800 block">Commission Payout Processed</p>
                  <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">Stripe ACH Transfer</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-black text-emerald-600 block">+$2,100.00</p>
                  <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">Apr 28, 2026</p>
                </div>
              </div>
              <div className="px-4 py-3 flex justify-between items-center hover:bg-slate-50 transition-colors">
                <div>
                  <p className="text-xs font-bold text-slate-800 block">New Tenant Activation</p>
                  <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">Entity: Echo Logistics</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-black text-slate-700 block">+$450 MRR</p>
                  <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">Apr 24, 2026</p>
                </div>
              </div>
              <div className="px-4 py-3 flex justify-between items-center hover:bg-slate-50 transition-colors">
                <div>
                  <p className="text-xs font-bold text-slate-800 block">System Login</p>
                  <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">IP: 192.168.1.1 (Seattle)</p>
                </div>
                <div className="text-right">
                  <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">Apr 24, 2026</p>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
      
      {/* EDIT PROFILE SLIDE-OVER */}
      {showEditForm && (
        <EditContractorSlideOver 
          contractor={contractor} 
          locations={locations}
          loadingLocations={loadingLocations}
          onClose={() => setShowEditForm(false)} 
          onSave={handleSaveProfile} 
        />
      )}
    </div>
  );
}